import { Activity, Dispatch, FunctionComponent, memo, RefObject, SetStateAction } from 'react';

import styles from '../custom-select.module.css';

import Portal from '@ui/portal/portal';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import useSetMenuPosition from '../helpers/use-set-menu-position';
import GlobalContainer from '@ui/container/global-container';
import CustomSelectMenuOption from './custom-select-menu-option';
import Text from '@ui/text/text/text';

type CustomSelectMenuProps = {
  menuOpen: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  selectRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  handlePreventDefaultClick: (e: React.MouseEvent<HTMLElement>) => void;
  selectableOptions: TReactSelectOption[];
  selectedOptions: TReactSelectOption[];
  setSelectedOptions: Dispatch<SetStateAction<TReactSelectOption[]>>;
  onAddNewSelect?: (inputValue: string) => void;
  RenderMenuOptionContent?: FunctionComponent<{ option: TReactSelectOption; idx: number; isSelected: boolean }>;
  RenderNoOptionsContent?: FunctionComponent;
  menuOptionSelectedIndicatorLabel?: string;
  onSelectOption?: (option: { option: TReactSelectOption; status: 'selected' | 'deselected' }) => void;
};

const CustomSelectMenu = memo(
  ({
    handlePreventDefaultClick,
    menuOpen,
    menuRef,
    selectableOptions,
    setSelectedOptions,
    selectedOptions,
    selectRef,
    onAddNewSelect,
    RenderMenuOptionContent,
    RenderNoOptionsContent,
    inputRef,
    menuOptionSelectedIndicatorLabel,
    onSelectOption,
  }: CustomSelectMenuProps) => {
    useSetMenuPosition({ controlRef: selectRef, menuRef, pause: !menuOpen, recalculate: menuOpen ? 1 : 0 });

    return (
      <Portal
        className="position-fixed top-0 height-0 overflow-hidden"
        inert={!menuOpen}
        style={{ zIndex: 1000 }}
        data-component="CustomSelectMenu"
      >
        <GlobalContainer
          className={`${styles.menu} ${menuOpen ? styles['menu-open'] : ''}`}
          onPointerDown={handlePreventDefaultClick}
          mode="GlobalCont1"
          ref={menuRef}
          role="menu"
          inert={!menuOpen}
        >
          <Activity mode={!selectableOptions.length ? 'visible' : 'hidden'}>
            {RenderNoOptionsContent ? (
              <RenderNoOptionsContent />
            ) : (
              <div className="padding-16">
                <Text className="S2" design="GlobalIcoText3">
                  No options found
                </Text>
              </div>
            )}
          </Activity>

          {menuOpen
            ? selectableOptions.map((option, idx) => (
                <CustomSelectMenuOption
                  key={option.value}
                  option={option}
                  idx={idx}
                  handlePreventDefaultClick={handlePreventDefaultClick}
                  setSelectedOptions={setSelectedOptions}
                  RenderMenuOptionContent={RenderMenuOptionContent}
                  selectRef={selectRef}
                  isSelected={!!selectedOptions.find((so) => so.value === option.value)}
                  menuOptionSelectedIndicatorLabel={menuOptionSelectedIndicatorLabel}
                  onSelectOption={onSelectOption}
                />
              ))
            : null}

          <Activity mode={onAddNewSelect ? 'visible' : 'hidden'}>
            <div
              role="option"
              aria-label="Add new option"
              className={`${styles.menuOption} ${styles.addNewOption}`}
              onClick={(e) => {
                handlePreventDefaultClick(e);
                onAddNewSelect?.(inputRef?.current?.value || '');
                const target = e.target;
                if (target instanceof HTMLElement) {
                  target.setAttribute('data-menu-list-option-active', 'false');
                }
              }}
              data-menu-list-option
              data-menu-list-option-active="false"
              data-menu-list-option-idx={selectableOptions.length}
              data-menu-list-option-value={'_add_new_'}
              data-menu-list-option-add-new
            >
              <Text design="LocalLinkPrim">+ Add New</Text>
            </div>
          </Activity>
        </GlobalContainer>
      </Portal>
    );
  }
);

CustomSelectMenu.displayName = 'CustomSelectMenu';

export default CustomSelectMenu;
